import _ from 'underscore';
import Backbone from 'backbone';
import ko from 'knockout';
import PageContext from '@bingads-webui-static-legacy/page-context';
import template from '@bingads-webui-campaign-legacy/bidding-strategy/template/setting.pug';
import ViewModel from '@bingads-webui-campaign-legacy/bidding-strategy/viewmodel/setting';
import types from '@bingads-webui-campaign-legacy/bidding-strategy/bidding-strategy-type';
import decimalNumber from '@bingads-webui-component-legacy/humanize/decimal';
import jsdata from '@bingads-webui-component-legacy/js-data-campaign/index';
import { getBidStrategyByCampaignTypeWhenPersonalizedOffersEnabled } from '@bingads-webui-campaign/personalized-offer-shared';
import { getBiddingStrategyForSponsoredCampaign } from '@bingads-webui-campaign/sponsored-campaign-shared';

import { PortfolioSelectorView } from '@bingads-webui-campaign-react/portfolio-bid-strategy-selector';

const i18n = window.globalLegacyI18n && window.globalLegacyI18n.get('module/extension/requirejs/i18n!global');

const defaultPopupIds = {
  BidStrategy: 'pop_BA_CampaignGrids_BidStrategy',
};

export default class View extends Backbone.View {
  initialize(options) {
    this.options = _.extend({
      entityType: 'Campaign',
      biddingStrategyType: types.manual,
      hideHeader: false,
    }, options);

    this.isPortfolioBidStrategyEnabled = _.result(_.result(_.result(PageContext, 'Permissions'), 'dynamic'), 'PortfolioBidStrategy', false);
    this.isPredictiveMatchingEnabled = _.result(_.result(_.result(PageContext, 'Permissions'), 'dynamic'), 'PredictiveMatching', false);
    this.isRSABidStrategyAutoTargetingEnabled = !this.isPredictiveMatchingEnabled && _.result(_.result(_.result(PageContext, 'Permissions'), 'dynamic'), 'RSABidStrategyAutoTargeting', false);

    if (this.isPortfolioBidStrategyEnabled && this.options.entityType === 'Campaign') {
      if (this.options.bidStrategyName) {
        // initialize model for portfolio bid strategy
        // when a campaign is associated with a portfolio bid strategy, it will be returned from MT with biddingStrategyType set to the portfolio bid strategy type
        // and an extra field bidStrategyName set to the selected portfolio bid strategy name
        this.options.portfolioBidStrategyType = this.options.biddingStrategyType;
        this.options.biddingStrategyType = types.portfolioBidStrategy;
      }
    }

    this.viewModel = new ViewModel(_.pick(
      this.options,
      'entityType',
      'biddingStrategyType',
      'biddingStrategyId',
      'bidStrategyName',
      'costPerSale',
      'portfolioBidStrategyType',
      'maxCpc',
      'targetCpa',
      'targetRoas',
      'targetAdPosition',
      'targetImpressionShare',
      'targetCommissionRate',
      'isPortfolio',
      'isInitialize',
      'isCreation',
      'isAfterSave',
      'campaignSettings',
      'campaignType',
      'isBulkEdit',
      'campaignSubType',
      'activityLogger',
      'i18nNewDesign',
      'getCampaignTypeGlobalFilter',
      'campaignBiddingStrategyType',
      'isMSStoreAdsGAWorkflow',
      'hideHeader',
      'autoTargetingEnabled',
      'validateWithBudget',
      'budgetAmount',
      'ccJsData',
      'isBroadMatchOnlyCampaign',
      'isRenderedInReact',
    ));
    this.template = template;
    this.popupIds = _.defaults({}, options.popupIds, defaultPopupIds);

    // set PO v2 options and value
    // set during initialization or we could not set the CPS that is not in the normal options list
    if (this.options.shouldSupportCashbackSettings) {
      this.setPersonalizedOffersOptionsAndValue({
        enabled: true,
        campaignType: this.options.campaignType,
      });
    }

    if (this.options.shouldShowSponsoredCampaignCashback) {
      this.setSponsoredCampaignOptionsAndValue({
        isSponsoredCampaignCashbackOptedIn: this.options.isSponsoredCampaignCashbackOptedIn,
        isSponsoredCampaignShowManualCpcOption: this.options.isSponsoredCampaignShowManualCpcOption,
      });
    }

    if (this.options.withRealTimeUpdates) {
      this.oldValueTracking = {};
      this.subscribeForRealTimeUpdates('selectedBidStrategy');
      this.subscribeForRealTimeUpdates('selectedPortfolioBidStrategy');
      this.subscribeForRealTimeUpdates('targetCpa');
      this.subscribeForRealTimeUpdates('isTargetCpaCheckboxSelected');
      this.subscribeForRealTimeUpdates('targetRoas');
      this.subscribeForRealTimeUpdates('isTargetRoasCheckboxSelected');
      this.subscribeForRealTimeUpdates('meetConversionGoal');
      this.subscribeForRealTimeUpdates('meetRevenueGoal');
      this.subscribeForRealTimeUpdates('showConversionCountCheckErrorMessage');
      this.subscribeForRealTimeUpdates('showRevenueCheckErrorMessage');
      this.subscribeForRealTimeUpdates('costPerSale');
      this.subscribeForRealTimeUpdates('maxCpc');
      this.subscribeForRealTimeUpdates('checkAutoTargeting');
      this.subscribeForRealTimeUpdates('checkCPC');
      this.subscribeForRealTimeUpdates('targetImpressionShare');
    }
  }

  render() {
    const element = this.$el;
    let prefixBiddingScheme = '';

    if (this.options.entityType === 'Campaign') {
      prefixBiddingScheme = 'Settings.';
    }

    element.html(this.template({
      i18n,
      PageContext,
      prefixBiddingScheme,
      popupIds: this.popupIds,
    }));
    if (element.length > 0) {
      ko.cleanNode(element[0]);
    }
    ko.applyBindings(this.viewModel, element[0]);
    this.triggerChangeEvent('render');

    return this;
  }

  ensurePortfolioSelectorView() {
    if (this.isPortfolioBidStrategyEnabled && !this.portfolioSelectorView) {
      if (!this.options.i18nNewDesign) {
        throw new Error('ensurePortfolioSelectorView: i18nNewDesign is missing');
      }

      this.portfolioSelectorView = new PortfolioSelectorView(
        {
          model: {
            campaignType: this.options.campaignType,
            bidStrategyName: this.options.bidStrategyName,
            isBulkEdit: this.options.isBulkEdit,
          },
          deps: {
            BiddingStrategyView: View,
            jsdata,
          },
          PageContext,
          i18n: this.options.i18nNewDesign,
          needConfirmManagePortfolio: this.options.needConfirmManagePortfolio,
          activityLogger: this.options.activityLogger,
        },
        (pbs) => this.onSelectPortfolioBidStrategy(pbs));

      this.$('.portfolio-selector-container').empty().html(this.portfolioSelectorView.render().$el);
    }
  }

  get events() {
    return {
      'change select.form-control': 'onBidStrategyChange',
      'click a.portfolio-bid-strategy-edit': 'enablePortfolioSelector',
    };
  }

  onSelectPortfolioBidStrategy(pbs) {
    this.viewModel.selectedPortfolioBidStrategy(pbs);
  }

  onBidStrategyChange(evt) {
    // Disable conversion checkin in automated bidding v1 to drive demand first
    // this.viewModel.checkConversionTracking();
    if (evt.target.value === types.portfolioBidStrategy && !this.options.bidStrategyName) {
      // if original bid strategy is not portfolio bid strategy, then display the selector
      this.ensurePortfolioSelectorView();
    }
  }

  enablePortfolioSelector() {
    this.viewModel.showPortfolioSelector(true);
    this.ensurePortfolioSelectorView();
  }

  action() {
    const selectedBidStrategy = this.viewModel.selectedBidStrategy();

    if (selectedBidStrategy === types.portfolioBidStrategy) {
      const selectedPortfolio = _.pick(this.viewModel.selectedPortfolioBidStrategy(), 'Id', 'Name');

      return {
        Actions: [{
          '@odata.type': '#Model.TextAction',
          'Property': 'BidStrategyId',
          'ActionType': 'Set',
          'Value': JSON.stringify(selectedPortfolio),
        }],
      };
    }

    const TypeId = types.biddingStrategyTypeId[selectedBidStrategy];
    let value = '{"Type":{0}'.replace('{0}', TypeId);

    if (this.viewModel.showCPC() && this.viewModel.checkCPC()) {
      value = value + ',"MaxCpc":{"Amount":{0}}'.replace('{0}', this.viewModel.maxCpc() ? decimalNumber.fromLocalToFloat(this.viewModel.maxCpc()) : 'null');
    }
    if (this.viewModel.showTargetCPA()) {
      value = value + ',"TargetCpa":{0}'.replace('{0}', this.viewModel.targetCpa() ? decimalNumber.fromLocalToFloat(this.viewModel.targetCpa()) : 'null');
    }
    if (this.viewModel.showTargetROAS()) {
      value = value + ',"TargetRoas":{0}'.replace('{0}', this.viewModel.targetRoas() ? decimalNumber.fromLocalToFloat(this.viewModel.targetRoas()) / this.viewModel.multiplier : 'null');
    }
    if (this.viewModel.showTargetImpressionShare()) {
      value = value + ',"TargetAdPosition":"{0}","TargetImpressionShare":{1}'
        .replace('{0}', this.viewModel.targetImpressionShareAdPosition())
        .replace('{1}', this.viewModel.targetImpressionShare() ? decimalNumber.fromLocalToFloat(this.viewModel.targetImpressionShare()) : 'null');
    }
    if (this.viewModel.showCostPerSale()) {
      value = value + ',"CostPerSale":{0}'.replace('{0}', this.viewModel.costPerSale() ? decimalNumber.fromLocalToFloat(this.viewModel.costPerSale()) : 'null');
    }

    if (this.viewModel.showCommissionRate()) {
      value = value + ',"CommissionRate":{0}'.replace('{0}', this.viewModel.targetCommissionRate() ? decimalNumber.fromLocalToFloat(this.viewModel.targetCommissionRate()) : 'null');
    }

    value += '}';

    const action = {
      Actions: [{
        '@odata.type': '#Model.TextAction',
        'Property': 'BiddingScheme',
        'ActionType': 'Set',
        'Value': value,
      }],
    };

    if (this.isRSABidStrategyAutoTargetingEnabled && this.viewModel.showAutoTargeting()) {
      action.Actions.push({
        '@odata.type': '#Model.TextAction',
        'Property': 'CampaignSettings/PredictiveTargetingEnabled',
        'ActionType': 'Set',
        'Value': this.viewModel.checkAutoTargeting() ? 'True' : 'False',
      });
    }

    if (this.isPredictiveMatchingEnabled && this.viewModel.showPredictiveMatching()) {
      action.Actions.push({
        '@odata.type': '#Model.TextAction',
        'Property': 'CampaignSettings/PredictiveTargetingEnabled',
        'ActionType': 'Set',
        'Value': this.viewModel.predictiveMatchingChecked() ? 'True' : 'False',
      });
    }

    return action;
  }

  update(model) {
    this.viewModel.selectedBidStrategy(model.Type);
    if (this.options.withRealTimeUpdates) {
      this.viewModel.update(model);
    }
  }

  validate() {
    return this.viewModel.validate();
  }

  getErrorMessages() {
    return this.viewModel.errorMessages();
  }

  getValues() {
    return this.viewModel.getValues();
  }

  onAdGroupTypeChanged(adGroupType) {
    let adGroupstrategyOptions = [{
      Value: types.manual,
      Text: i18n.getString('BidStrategy_Option_Manual'),
    }];

    if (adGroupType === 'BidBoost') {
      // Only manual strategy is allowed for bid boost ad group
      this.viewModel.strategyOptions(adGroupstrategyOptions);
      this.viewModel.selectedBidStrategy(types.manual);
    } else {
      adGroupstrategyOptions.unshift({
        Value: types.parent,
        Text: i18n.getString('BidStrategy_Option_ParentCampaign'),
      });

      this.viewModel.strategyOptions(adGroupstrategyOptions);
      this.viewModel.selectedBidStrategy(types.parent);
    }
  }

  setPersonalizedOffersOptionsAndValue({ enabled, campaignType = this.options.campaignType }) {
    if (PageContext.Permissions.IsPersonalizedOffersV2Enabled ||
      PageContext.Permissions.IsPersonalizedOffersV3Enabled ||
      PageContext.Permissions.IsCostPerSaleEnabled) {
      this.viewModel.isPersonalizedOffersEnabled(enabled);
      if (enabled) {
        const { targetBidStrategy, bidStrategyOptions } = getBidStrategyByCampaignTypeWhenPersonalizedOffersEnabled({
          campaignType,
          i18n,
          Permissions: PageContext.Permissions,
          isAfterSave: this.viewModel.isAfterSave(),
          selectedBidStrategy: this.viewModel.selectedBidStrategy(),
        });

        this.viewModel.strategyOptions(bidStrategyOptions);
        // if selectedBidStrategy is not included in the bidStaregyOptions, use the targetBidStrategy
        if (!bidStrategyOptions.includes(this.viewModel.selectedBidStrategy())) {
          this.viewModel.selectedBidStrategy(targetBidStrategy);
        }
      } else {
        this.updateBidStrategy();
      }
    }
  }

  setSponsoredCampaignOptionsAndValue({
    isSponsoredCampaignCashbackOptedIn: isSponsoredCampaignCashbackEnabled = false,
    isSponsoredCampaignShowManualCpcOption: isShowManualBidStrategy = false,
  } = {},
  ) {
    const isMranEcpcEnabled = _.result(PageContext, ['Permissions', 'dynamic', 'MranEcpc'], false);
    const isMranTroasEnabled = _.result(PageContext, ['Permissions', 'dynamic', 'MranTroas'], false);
    const { targetBidStrategy, bidStrategyOptions, showBidStrategySection, forceReadOnly } = getBiddingStrategyForSponsoredCampaign({
      isSponsoredCampaignCashbackEnabled,
      isMranEcpcEnabled,
      isMranTroasEnabled,
      isShowManualBidStrategy,
      i18n,
      isMSStoreAdsGAWorkflow: this.options.isMSStoreAdsGAWorkflow,
    });

    this.viewModel.strategyOptions(bidStrategyOptions);
    this.viewModel.selectedBidStrategy(bidStrategyOptions.find((v) => v.Value === this.options.biddingStrategyType) ? this.options.biddingStrategyType : targetBidStrategy);
    this.viewModel.forceHideBidStrategySection(!showBidStrategySection);
    this.viewModel.forceReadOnly(forceReadOnly);
  }

  subscribeForRealTimeUpdates(source) {
    const observable = this.viewModel[source];

    this.oldValueTracking[source] = observable();
    observable.subscribe((oldValue) => {
      this.oldValueTracking[source] = oldValue;
    }, undefined, 'beforeChange');
    observable.subscribe((newValue) => {
      this.triggerChangeEvent(source, this.oldValueTracking[source], newValue);
      this.oldValueTracking[source] = newValue;
    }, undefined, 'change');
  }

  triggerChangeEvent(source, oldValue, newValue) {
    if (this.options.withRealTimeUpdates) {
      const isFromInitialization =
        source === 'render' ||
        (source === 'meetConversionGoal' && oldValue === -1 && newValue !== -1) ||
        (source === 'meetRevenueGoal' && oldValue === -1 && newValue !== -1) ||
        (source === 'showConversionCountCheckErrorMessage' && this.oldValueTracking.meetConversionGoal === -1) ||
        (source === 'showRevenueCheckErrorMessage' && this.oldValueTracking.meetRevenueGoal === -1);

      this.trigger('change', this.getValues(), this.validate(), isFromInitialization);
    }
  }

  updateBidStrategy(isCoOpStore) {
    // for shopping campaigns, when a co-op store is selected,
    // the only bidding type shown should be manual.
    // otherwise, the already decided campaign bidding strategy options are shown.

    const manualOnlyOption = [{
      Value: types.manual,
      Text: i18n.getString('BidStrategy_Option_Manual_Campaign'),
    }];

    if (isCoOpStore) {
      this.viewModel.strategyOptions(manualOnlyOption);
      this.viewModel.selectedBidStrategy(types.manual);
    } else {
      this.viewModel.strategyOptions(this.viewModel.campaignBiddingStrategyOptions);

      if (this.options.campaignSubType === 'ShoppingSmartAds') {
        this.viewModel.selectedBidStrategy(types.maxConversionValue);
      } else if (this.viewModel.isCreation) {
        this.viewModel.selectedBidStrategy(types.enhancedCPC);
      }
    }
  }
}
